import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { TrendingUp, Award, Target, DollarSign, Zap, Star, Trophy } from 'lucide-react';

const SalesToolsDashboard = ({ quotes, companyColors, company }) => {
  const primary = companyColors?.primary || '#3b82f6';
  const COMMISSION_RATE = company?.commissionRate ?? 0.10;
  const MONTHLY_GOAL = company?.monthlyGoal ?? 50000;
  const now = new Date();
  const thisMonth = quotes?.filter(q => {
    const d = new Date(q.createdAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }) || [];

  const accepted = quotes?.filter(q => q.status === 'Angenommen') || [];
  const acceptedThisMonth = accepted.filter(q => {
    const d = new Date(q.acceptedAt || q.createdAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const totalRevenue = accepted.reduce((s, q) => s + (q.total || 0), 0);
  const revenueThisMonth = acceptedThisMonth.reduce((s, q) => s + (q.total || 0), 0);
  const commissionTotal = totalRevenue * COMMISSION_RATE;
  const commissionThisMonth = revenueThisMonth * COMMISSION_RATE;
  const goalProgress = Math.min(100, (revenueThisMonth / MONTHLY_GOAL) * 100);

  // Gamification: compute achievements
  const achievements = [];
  if (accepted.length >= 1)  achievements.push({ icon: Trophy, label: 'Erster Abschluss', desc: 'Erstes Angebot angenommen', color: 'yellow' });
  if (accepted.length >= 5)  achievements.push({ icon: Star, label: 'Auf Kurs', desc: '5 Angebote abgeschlossen', color: 'blue' });
  if (accepted.length >= 10) achievements.push({ icon: Award, label: 'Profi', desc: '10+ Abschlüsse', color: 'purple' });
  if (totalRevenue >= 10000) achievements.push({ icon: DollarSign, label: '10k Club', desc: '10.000€ Umsatz erreicht', color: 'green' });
  if (totalRevenue >= 50000) achievements.push({ icon: Zap, label: '50k Profi', desc: '50.000€ Umsatz erreicht', color: 'orange' });
  if (goalProgress >= 100)   achievements.push({ icon: Target, label: 'Monatsziel', desc: 'Monatsziel erreicht!', color: 'green' });

  // Top 5 quotes by value
  const topQuotes = [...(accepted)].sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5);

  const fmt = (n) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const colorMap = {
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
  };

  return (
    <div className="space-y-6" data-testid="sales-tools-dashboard">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${primary}20` }}>
          <TrendingUp className="w-5 h-5" style={{ color: primary }} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Vertrieb & Provision</h2>
          <p className="text-slate-500 text-sm">Ihre persönliche Verkaufs-Performance</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Provision gesamt', value: `€${fmt(commissionTotal)}`, sub: `${COMMISSION_RATE*100}% von €${fmt(totalRevenue)}`, icon: DollarSign, color: 'green' },
          { label: 'Provision diesen Monat', value: `€${fmt(commissionThisMonth)}`, sub: `aus €${fmt(revenueThisMonth)} Umsatz`, icon: TrendingUp, color: 'blue' },
          { label: 'Abschlüsse gesamt', value: accepted.length, sub: `${thisMonth.length} neue Angebote diesen Monat`, icon: Award, color: 'purple' },
          { label: 'Abschlussquote', value: `${quotes?.length ? Math.round((accepted.length / quotes.length) * 100) : 0}%`, sub: `${accepted.length} von ${quotes?.length || 0} Angeboten`, icon: Target, color: 'orange' },
        ].map((kpi, i) => (
          <Card key={i} className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 bg-${kpi.color}-50`}>
                <kpi.icon className={`w-5 h-5 text-${kpi.color}-600`} />
              </div>
              <div className="text-2xl font-bold text-slate-900">{kpi.value}</div>
              <div className="text-xs text-slate-500 font-medium mt-1">{kpi.label}</div>
              <div className="text-xs text-slate-400 mt-0.5">{kpi.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Monthly Goal Progress */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold text-slate-900">Monatsziel</h3>
              <p className="text-sm text-slate-500">Ziel: €{fmt(MONTHLY_GOAL)} Umsatz im {now.toLocaleString('de-DE', { month: 'long' })}</p>
            </div>
            <Badge className={goalProgress >= 100 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
              {goalProgress.toFixed(0)}% erreicht
            </Badge>
          </div>
          <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-1000"
              style={{ width: `${goalProgress}%`, background: goalProgress >= 100 ? '#22c55e' : primary }}
            />
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-2">
            <span>€{fmt(revenueThisMonth)} erreicht</span>
            <span>€{fmt(Math.max(0, MONTHLY_GOAL - revenueThisMonth))} fehlen noch</span>
          </div>
        </CardContent>
      </Card>

      {/* Achievements */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" /> Achievements
          </CardTitle>
        </CardHeader>
        <CardContent>
          {achievements.length === 0 ? (
            <p className="text-slate-400 text-sm">Noch keine Achievements freigeschaltet. Erste Angebote abschließen!</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {achievements.map((a, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${colorMap[a.color]}`}
                  data-testid={`achievement-${a.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <a.icon className="w-5 h-5 shrink-0" />
                  <div>
                    <div className="font-semibold text-sm">{a.label}</div>
                    <div className="text-xs opacity-75">{a.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Locked achievements hint */}
          {achievements.length < 6 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-400">{6 - achievements.length} weitere Achievements freizuschalten</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Angebote */}
      {topQuotes.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Star className="w-5 h-5 text-blue-500" /> Top-Abschlüsse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topQuotes.map((q, i) => (
                <div key={q.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                      style={{ background: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : primary }}>
                      {i + 1}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{q.customerData?.company || q.customer?.company || 'Unbekannt'}</div>
                      <div className="text-xs text-slate-500">{q.quoteNumber} · {new Date(q.acceptedAt || q.createdAt).toLocaleDateString('de-DE')}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-slate-900">€{fmt(q.total || 0)}</div>
                    <div className="text-xs text-green-600">Provision: €{fmt((q.total || 0) * COMMISSION_RATE)}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SalesToolsDashboard;
